import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  colors,
  MAX_CONTENT_WIDTH,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';
import Icon, { IconName } from '../ui/Icon';
import Text from '../ui/Text';

export type TabKey = 'home' | 'services' | 'bookings' | 'cart' | 'profile' | 'chat';

type Tab = {
  key: TabKey;
  label: string;
  icon: IconName;
  activeIcon: IconName;
};

/**
 * Chat has a tab, but it is not shown yet.
 *
 * The screen works and is reachable - Message on a booking opens that
 * professional's thread directly - so this hides the front door rather than
 * removing the room. Flip to `true` to bring the tab back.
 */
const SHOW_CHAT = false;

const ALL_TABS: Tab[] = [
  { key: 'home', label: 'Home', icon: 'home', activeIcon: 'homeFilled' },
  /*
   * Its own tab now rather than a sheet reached from Home - see the note on
   * `ServiceSheet`/`CatalogueSheet` in `HomeScreen.tsx`. Browsing the
   * catalogue is a destination, not a glance over Home.
   */
  {
    key: 'services',
    label: 'Services',
    icon: 'grid',
    activeIcon: 'grid',
  },
  /*
   * Cart sits in the middle, not beside Profile.
   *
   * It is the tab reached most often mid-task and the one the marker sits
   * under most, and the centre of a five-tab bar is both the easiest place to
   * hit with either thumb and the position the dome reads as "home base".
   *
   * No filled variant exists for `cart`; the marker and the ink/subtle colour
   * change are what carry the selected state here instead.
   */
  { key: 'cart', label: 'Cart', icon: 'cart', activeIcon: 'cart' },
  {
    key: 'bookings',
    label: 'Bookings',
    icon: 'calendar',
    activeIcon: 'calendarFilled',
  },
  { key: 'profile', label: 'Profile', icon: 'user', activeIcon: 'userFilled' },
  /*
   * Chat, not a dial button.
   *
   * Support already has a home in Account, so a phone tab was a second door to
   * the same place. Messaging the professional who is coming is a different
   * thing, and the deck's own framing of it - "Customer support -> Someone
   * answers" - is a conversation rather than a call.
   */
  { key: 'chat', label: 'Chat', icon: 'chat', activeIcon: 'chatFilled' },
];

/* Five tabs today: Home, Services, Cart, Bookings, Profile. Everything below
   reads `TABS.length`, so the marker and the tab widths follow on their own. */
const TABS: Tab[] = ALL_TABS.filter(tab => tab.key !== 'chat' || SHOW_CHAT);

/**
 * Horizontal breathing room inside the bar.
 *
 * Zero, after measuring. Padding here shrinks every cell equally, which pulls
 * the outer WORDS further from the bar's ends rather than closer - the
 * opposite of what it looks like it should do. Kept as a named constant
 * because the marker arithmetic below has to account for it either way.
 */
const BAR_INSET = 0;

/** Width of the marker. Wider than tall, so it reads as a low dome. */
const BULGE = 46;
/** How far the dome's flat base sits below the bar's inner bottom edge. */
const BULGE_DROP = 6;
const BULGE_HEIGHT = 15;
const BAR_HEIGHT = 64;

/**
 * What a scrolling screen must leave clear at its foot.
 *
 * The bar floats over the content rather than sitting in the column, so every
 * screen under it has to pad for it. Exported so that padding is derived from
 * the bar's real height rather than from a number typed once and left behind
 * when the bar changed - the gap it leaves is only correct by accident.
 *
 * Add the bottom safe-area inset to this: the bar clears the gesture bar or
 * the nav buttons, and that varies by device.
 */
export const NAV_BAR_HEIGHT = BAR_HEIGHT + spacing.sm;

type Props = {
  active: TabKey;
  onSelect: (key: TabKey) => void;
  /** Shown as a dot on the Cart tab - what used to sit on the header's cart icon. */
  cartCount?: number;
  /**
   * Drop the Services tab entirely - for an address we do not serve.
   *
   * The tab's only job is picking a service to book, and there is nothing
   * bookable at this address. Leaving it in the bar is a door onto a screen
   * that can only repeat the same "not here yet" message, so the door goes.
   *
   * The marker arithmetic below measures against the tabs actually rendered,
   * so removing one re-spaces the bar rather than leaving a gap.
   */
  hideServices?: boolean;
  /**
   * Desktop web only - see the note above `webDesktop` in the component.
   * The hamburger menu absorbs the header's wallet and notification actions,
   * so their handlers arrive here on every tab. Ignored by the bar.
   */
  onPressWallet?: () => void;
  onPressNotifications?: () => void;
  notificationCount?: number;
};

/** One row of the desktop hamburger menu. */
function MenuRow({
  icon,
  label,
  active,
  badge = 0,
  onPress,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  badge?: number;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="menuitem"
      accessibilityState={{ selected: active === true }}
      accessibilityLabel={badge > 0 ? `${label}, ${badge}` : label}
      onPress={onPress}
      style={({ pressed }) => [
        styles.menuRow,
        active ? styles.menuRowActive : null,
        pressed ? styles.pressed : null,
      ]}
    >
      <Icon
        name={icon}
        size={20}
        color={active ? colors.ink : colors.inkBody}
      />
      <Text
        variant="body"
        color={active ? colors.ink : colors.inkBody}
        style={styles.menuLabel}
      >
        {label}
      </Text>
      {badge > 0 ? (
        <View style={styles.badge}>
          <Text variant="caption" color={colors.onPrimary} style={styles.badgeText}>
            {badge > 9 ? '9+' : badge}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

/**
 * Floating tab bar with a domed marker riding the inside of its lower edge,
 * under whichever tab is selected.
 *
 * The marker sits inside the bar rather than hanging off it. Outside, it had
 * to clear the system navigation as well as the bar's own shadow, and on a
 * phone with three-button navigation it ran straight into the buttons.
 *
 * Marker and selected icon are the same ink, and it is the darkest step in the
 * ramp rather than the brand green - against the mid-green used for the
 * inactive tabs, brand green was too close to read as "you are here".
 *
 * Each tab is captioned. The marker says where you are; the caption says what
 * the tab is, which no icon reliably does - a handset reads as "call" to some
 * people and "support" to others.
 */
function BottomNav({
  active,
  onSelect,
  cartCount = 0,
  hideServices = false,
  onPressWallet,
  onPressNotifications,
  notificationCount = 0,
}: Props) {
  const insets = useSafeAreaInsets();
  const resp = useResponsive();

  const [barWidth, setBarWidth] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const slide = useRef(new Animated.Value(0)).current;

  /* The tabs actually drawn. Everything below measures against THIS rather
     than the module-level list, so dropping one re-spaces the bar and moves
     the marker with it instead of leaving a hole. */
  const tabs = useMemo(
    () => (hideServices ? TABS.filter(tab => tab.key !== 'services') : TABS),
    [hideServices],
  );

  /*
   * Desktop web wears a hamburger, not a thumb bar - see the return below.
   * Declared here, USED after every hook: an early return above the marker
   * effect would change the hook count when a browser window is resized
   * across the tablet breakpoint, which React treats as a broken component.
   */
  const webDesktop = Platform.OS === 'web' && resp.isTablet;

  const activeIndex = Math.max(
    0,
    tabs.findIndex(tab => tab.key === active),
  );
  /* Inside the bar's own padding - see `bar.paddingHorizontal`. `onLayout`
     reports the outer width, so the padding has to come off before the row is
     divided or the marker drifts further right with every tab. */
  const tabWidth = Math.max(0, barWidth - BAR_INSET * 2) / tabs.length;

  useEffect(() => {
    if (tabWidth <= 0) {
      return;
    }
    // Centre of the selected tab, less half the marker. Offset by the bar's
    // padding, which the marker sits outside of.
    const target = BAR_INSET + tabWidth * (activeIndex + 0.5) - BULGE / 2;

    const glide = Animated.spring(slide, {
      toValue: target,
      useNativeDriver: true,
      speed: 14,
      bounciness: 6,
    });
    glide.start();
    return () => glide.stop();
  }, [activeIndex, slide, tabWidth]);

  const onBarLayout = (event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.width;
    setBarWidth(prev => (Math.abs(prev - measured) > 0.5 ? measured : prev));
  };

  /*
   * Desktop web wears a hamburger, not a thumb bar.
   *
   * The bar is chrome sized by a hand holding a phone; under a mouse pointer
   * at the foot of a monitor it is five tiny targets a screen-height from
   * where the cursor lives. On a wide web viewport the same destinations -
   * plus the wallet and notifications the header gives up there, see
   * `HomeHeader` - move into a menu behind a top-right button, where every
   * website keeps it. Everything phone-shaped (native, and a phone-width
   * browser) keeps the bar untouched.
   */
  if (webDesktop) {
    const choose = (key: TabKey) => {
      setMenuOpen(false);
      onSelect(key);
    };

    return (
      <>
        <View style={styles.menuAnchor} pointerEvents="box-none">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Menu"
            onPress={() => setMenuOpen(true)}
            style={({ pressed }) => [
              styles.menuButton,
              pressed ? styles.pressed : null,
            ]}
          >
            <View style={styles.menuGlyphBar} />
            <View style={styles.menuGlyphBar} />
            <View style={styles.menuGlyphBar} />
            {cartCount + notificationCount > 0 ? (
              <View style={styles.menuButtonDot} />
            ) : null}
          </Pressable>
        </View>

        <Modal
          transparent
          visible={menuOpen}
          animationType="fade"
          onRequestClose={() => setMenuOpen(false)}
        >
          <Pressable
            style={styles.menuBackdrop}
            accessibilityRole="button"
            accessibilityLabel="Close menu"
            onPress={() => setMenuOpen(false)}
          >
            {/* Stops row taps from also hitting the closing backdrop. */}
            <Pressable style={styles.menuCard} onPress={() => {}}>
              {/* Every tab, Home included - with the bar gone, this menu is
                  the only way back from Services or Cart on the desktop. */}
              {tabs.map(tab => (
                <MenuRow
                  key={tab.key}
                  icon={tab.key === active ? tab.activeIcon : tab.icon}
                  label={tab.label}
                  active={tab.key === active}
                  badge={tab.key === 'cart' ? cartCount : 0}
                  onPress={() => choose(tab.key)}
                />
              ))}

              <View style={styles.menuDivider} />

              <MenuRow
                icon="wallet"
                label="Wallet"
                onPress={() => {
                  setMenuOpen(false);
                  onPressWallet?.();
                }}
              />
              <MenuRow
                icon="bell"
                label="Notifications"
                badge={notificationCount}
                onPress={() => {
                  setMenuOpen(false);
                  onPressNotifications?.();
                }}
              />
            </Pressable>
          </Pressable>
        </Modal>
      </>
    );
  }

  return (
    <View
      style={[
        styles.wrap,
        /*
         * A browser draws its own chrome below the page, so there is no
         * gesture bar or button row to clear - the float that protects the
         * bar from those on a handset read as the bar stopping short of the
         * screen. On web the bar sits flush on the bottom edge instead,
         * gutter to gutter.
         */
        Platform.OS === 'web'
          ? styles.wrapWeb
          : // Clear of the gesture bar or the three nav buttons, which the bar
            // was otherwise sitting flush against.
            { paddingBottom: Math.max(insets.bottom, spacing.md) + spacing.sm },
      ]}
      pointerEvents="box-none"
    >
      <View style={styles.row}>
        <View
          style={[styles.bar, Platform.OS === 'web' ? styles.barWeb : null]}
          onLayout={onBarLayout}
        >
          {/* First child, so the tabs paint over it. */}
          <Animated.View
            style={[
              styles.bulge,
              {
                opacity: barWidth > 0 ? 1 : 0,
                transform: [{ translateX: slide }],
              },
            ]}
            pointerEvents="none"
          />

          {tabs.map(tab => {
            const selected = tab.key === active;
            const badge = tab.key === 'cart' ? cartCount : 0;

            return (
              <Pressable
                key={tab.key}
                accessibilityRole="tab"
                accessibilityState={{ selected }}
                accessibilityLabel={
                  badge > 0 ? `${tab.label}, ${badge} items` : tab.label
                }
                onPress={() => onSelect(tab.key)}
                style={({ pressed }) => [styles.tab, pressed ? styles.pressed : null]}
              >
                <View>
                  <Icon
                    name={selected ? tab.activeIcon : tab.icon}
                    size={21}
                    color={selected ? colors.ink : colors.inkSubtle}
                  />
                  {badge > 0 ? (
                    <View style={styles.badge}>
                      <Text variant="caption" color={colors.onPrimary} style={styles.badgeText}>
                        {badge > 9 ? '9+' : badge}
                      </Text>
                    </View>
                  ) : null}
                </View>
                {/* Centred and clipped to its own cell. Left to size itself,
                    a long label spills past the cell it belongs to and pushes
                    against its neighbour, which is what made the gaps look
                    uneven even once the cells were equal. */}
                <Text
                  variant="eyebrow"
                  numberOfLines={1}
                  color={selected ? colors.ink : colors.inkSubtle}
                  style={styles.label}
                >
                  {tab.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: screenPadding,
    backgroundColor: 'transparent',
  },
  /* Web only - see the ternary in the component. Flush to the page's bottom
     edge and to its sides; the row's own `maxWidth` still holds the bar to a
     hand's width on anything wider than a phone. */
  wrapWeb: {
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  /*
   * Held to a phone's width and centred.
   *
   * Five tabs spread across a 10-inch screen are five icons a hand's span
   * apart, with the thumb that has to reach them anchored at one corner. The
   * bar is chrome, and chrome is sized by the hand rather than by the display.
   * `maxWidth` never binds on a phone, where the row is already narrower.
   */
  row: {
    height: BAR_HEIGHT,
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
  },
  bar: {
    height: BAR_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    /*
     * NOT `space-between`.
     *
     * That distributes the LEFTOVER space between cells, so cells of unequal
     * content width end up unevenly spaced - which is exactly the bug it was
     * added to fix: "HOME SERVICES BOOKINGS" bunched up while "CART PROFILE"
     * drifted apart, because the long labels ate the slack near them. With
     * every tab on an identical `flexBasis: 0` there is no leftover to
     * distribute, and the default packing gives a uniform pitch.
     */
    /*
     * Inset so the first and last words are not stranded against the bar's
     * rounded ends.
     *
     * Without it the cells run edge to edge, and because "HOME" and "PROFILE"
     * are narrower than their cells the leftover piles up outside them -
     * 26.5dp to the left of HOME and 17.3dp to the right of PROFILE, against
     * inner gaps of 28-44dp. The corners then read as dead space.
     *
     * Padding here shrinks every cell equally, so the pitch stays uniform and
     * the whole row draws in toward the middle.
     */
    paddingHorizontal: BAR_INSET,
    borderRadius: radii.xl,
    backgroundColor: colors.surface,
    // Keeps the marker within the rounded corners at the end tabs. Safe now
    // that nothing is meant to escape the bar.
    overflow: 'hidden',
    ...shadows.md,
  },
  /* Web only. A rounded corner sitting ON the screen edge shows a sliver of
     the page through the gap it leaves; squared where the bar meets the
     bottom, still rounded where it meets the content above. */
  barWeb: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  /* Lifted off centre by the marker's height, so icon and caption sit in the
     middle of the space above the dome rather than in the middle of the bar. */
  tab: {
    /*
     * Equal cells, whatever the label says.
     *
     * `flexBasis: 0` is what makes this true rather than approximately true:
     * without it the label's own width becomes the cell's starting size, so
     * "SERVICES" claims more of the row than "CART" and the icons stop
     * sitting on an even pitch. With a zero basis the row is divided by count
     * alone, and each icon lands on the centre of an identical cell.
     */
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    paddingBottom: BULGE_HEIGHT - BULGE_DROP,
  },
  label: {
    width: '100%',
    textAlign: 'center',
    /*
     * 9pt, and that size is doing the whole job.
     *
     * ---------------------------------------------------------------------
     * WHY THERE ARE NO PER-TAB OFFSETS ANY MORE
     * ---------------------------------------------------------------------
     * The cells are identical (77.4dp on a 411dp screen) and each label is
     * centred in its own, so the CENTRES are already even. What is not even
     * is the whitespace between words, because that is the pitch minus half
     * of each label - and "SERVICES"/"BOOKINGS" are twice the width of
     * "CART". At the inherited 11pt/1.1 the tightest gap was 11.6dp against
     * 32.5dp elsewhere, which is the bunching this bar had.
     *
     * The obvious fix - shift the crowded labels apart - was tried and was
     * worse. Offsetting a label moves it out from under its own icon, so
     * "BOOKINGS" sat 9dp right of the calendar above it. Equal gaps bought
     * with visibly misaligned columns is not a trade worth making: a word
     * belongs under its icon before it belongs at a tidy distance from its
     * neighbour.
     *
     * Shrinking the type fixes it without moving anything. At 9pt/0.2 the
     * widest label is 48.9dp, every word stays centred under its icon, and
     * the tightest gap is 28.5dp - comfortable, and the remaining variation
     * is the honest consequence of "CART" being shorter than "BOOKINGS".
     */
    fontSize: 9,
    letterSpacing: 0.2,
    /* Cancels the trailing letter-space, which is applied after the last
       glyph as well as between them and would otherwise leave the text
       sitting a fraction left of its box's centre. */
    marginRight: -0.2,
  },
  pressed: {
    opacity: 0.55,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -8,
    minWidth: 16,
    height: 16,
    paddingHorizontal: 3,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.danger,
  },
  badgeText: {
    fontSize: 10,
    lineHeight: 12,
  },
  /*
   * A dome standing on the bar's inner bottom edge, anchored to its left and
   * moved along by `translateX`. Rounded on top only, so its flat side reads
   * as sitting on the edge rather than floating.
   */
  bulge: {
    position: 'absolute',
    left: 0,
    // Dropped below the bar's inner edge so only the crown of the dome shows.
    bottom: -BULGE_DROP,
    width: BULGE,
    height: BULGE_HEIGHT,
    // Half the width, so the crown stays a true arc at this wider size rather
    // than flattening into a rounded rectangle.
    borderTopLeftRadius: BULGE / 2,
    borderTopRightRadius: BULGE / 2,
    backgroundColor: colors.surfaceInverse,
  },

  /* --------------------------------------------------------------------- *
   * The desktop-web hamburger - see `webDesktop` in the component.
   * --------------------------------------------------------------------- */
  /* Pinned to the screen's top-right corner, where `HomeHeader`'s bell and
     wallet sat before they moved into this menu. */
  menuAnchor: {
    position: 'absolute',
    top: spacing.md,
    right: screenPadding,
    zIndex: 40,
  },
  menuButton: {
    width: 44,
    height: 44,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: colors.surface,
    ...shadows.md,
  },
  menuGlyphBar: {
    width: 18,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.ink,
  },
  /* Says "something in here wants attention" while the menu is shut. */
  menuButtonDot: {
    position: 'absolute',
    top: 9,
    right: 9,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.danger,
  },
  menuBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(14, 42, 31, 0.28)',
  },
  /* Anchored under the button rather than centred: a menu belongs to the
     control that opened it. */
  menuCard: {
    position: 'absolute',
    top: spacing.md + 52,
    right: screenPadding,
    minWidth: 240,
    padding: spacing.sm,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.md,
  },
  menuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.md,
  },
  menuRowActive: {
    backgroundColor: colors.surfaceMuted,
  },
  menuLabel: {
    flex: 1,
  },
  menuDivider: {
    height: 1,
    marginVertical: spacing.xs,
    marginHorizontal: spacing.sm,
    backgroundColor: colors.border,
  },
});

/*
 * Memoised.
 *
 * The bar is now mounted once and kept across every tab change, so it re-renders
 * whenever anything on the page above it moves - a keystroke in the search
 * field, a quantity in the cart. Its own props change only when the tab does,
 * and its marker runs a spring on every render it does not skip.
 */
export default React.memo(BottomNav);
